import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["digit", "pinHidden", "submit", "loading", "loadingDots"]

  // "블록체인 처리 중" 뒤 점(...) 애니메이션 타이머 핸들
  #dotsTimer = null

  connect() {
    this.#setupDigitInputs()
    // 폼 제출 종료(turbo:submit-end) 시 처리.
    // PIN 오류처럼 폼이 DOM에 그대로 남는 경우 로딩 스피너를 원복하고 재입력할 수 있게 한다.
    // 성공 시에는 서버가 slot-detail-modal 프레임 전체를 교체 → 이 컨트롤러가 사라지므로 호출되지 않는다.
    this.boundSubmitEnd = this.#onSubmitEnd.bind(this)
    this.element.addEventListener("turbo:submit-end", this.boundSubmitEnd)
  }

  disconnect() {
    // connect에서 등록한 리스너 + 점 애니메이션 타이머 정리 (중복 등록·메모리 누수 방지)
    if (this.boundSubmitEnd) {
      this.element.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
    }
    this.#stopDots()
  }

  // PIN 제출 시: 제출 버튼을 숨기고, 모달 전체를 덮는 "블록체인 처리 중" 스핀 오버레이를 띄운다.
  // 블록체인 트랜잭션(A서버→B서버 gRPC→체인)이 수초~수십초 걸릴 수 있으므로 레이턴시 동안 대기 UX 제공.
  handleSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.style.display = "none"
    }
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = "flex"
    }
    this.#startDots()
  }

  // PIN 오류 등으로 폼이 남은 경우: 오버레이·점 애니메이션을 멈추고 제출 버튼을 다시 노출한 뒤,
  // 입력값을 비우고 첫 칸에 포커스 → 사용자가 즉시 재입력 가능.
  // (성공 시엔 서버가 모달 프레임 전체를 교체하므로 이 콜백은 호출되지 않음)
  #onSubmitEnd() {
    this.#stopDots()
    if (this.hasLoadingTarget) this.loadingTarget.style.display = "none"
    if (this.hasSubmitTarget) this.submitTarget.style.display = ""
    this.#clearDigits()
  }

  // "블록체인 처리 중" 뒤 점을 1→2→3개로 순환 표시 (0.5초 간격) — 처리가 살아있음을 시각화
  #startDots() {
    if (!this.hasLoadingDotsTarget) return
    this.#stopDots()
    let count = 0
    this.#dotsTimer = setInterval(() => {
      this.loadingDotsTarget.textContent = ".".repeat((count % 3) + 1)
      count++
    }, 500)
  }

  #stopDots() {
    if (this.#dotsTimer) {
      clearInterval(this.#dotsTimer)
      this.#dotsTimer = null
    }
    if (this.hasLoadingDotsTarget) this.loadingDotsTarget.textContent = ""
  }

  // 6칸 입력을 모두 비우고 상태 동기화(#syncPin이 제출 버튼을 다시 비활성화) 후 첫 칸 포커스
  #clearDigits() {
    this.digitTargets.forEach((d) => { d.value = "" })
    this.#syncPin()
    this.digitTargets[0]?.focus()
  }

  #setupDigitInputs() {
    this.digitTargets.forEach((digit, idx) => {
      digit.addEventListener("input", (e) => {
        const val = e.target.value.replace(/\D/g, "")
        e.target.value = val.slice(0, 1)
        if (val && idx < 5) this.digitTargets[idx + 1]?.focus()
        this.#syncPin()
      })

      digit.addEventListener("keydown", (e) => {
        if (e.key === "Backspace" && !e.target.value && idx > 0) {
          this.digitTargets[idx - 1].focus()
          this.digitTargets[idx - 1].value = ""
          this.#syncPin()
        }
        if (!/^\d$/.test(e.key) && !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)) {
          e.preventDefault()
        }
      })

      digit.addEventListener("paste", (e) => {
        e.preventDefault()
        const pasted = (e.clipboardData || window.clipboardData)
          .getData("text").replace(/\D/g, "").slice(0, 6)
        pasted.split("").forEach((ch, i) => {
          if (this.digitTargets[i]) this.digitTargets[i].value = ch
        })
        this.digitTargets[Math.min(pasted.length, 5)]?.focus()
        this.#syncPin()
      })
    })
  }

  #syncPin() {
    const pin = this.digitTargets.map(d => d.value).join("")
    if (this.hasPinHiddenTarget) this.pinHiddenTarget.value = pin
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = pin.length !== 6
      this.submitTarget.style.opacity = pin.length === 6 ? "1" : "0.4"
    }
  }
}
